import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AudioRecorder from '../../../components/Chat/AudioRecorder';
import {
  getAvailableTutorsForStudent,
  getOrCreateChatBox,
  getUnreadMessageCount,
  listenToMessages,
  listenToUserChatBoxes,
  markMessagesAsRead,
  sendMessage,
  uploadMedia,
  MEDIA_TYPES
} from '../../../../store/services/chatService';

import MediaAttachmentModal from '../../../components/Chat/MediaAttachmentModal.jsx';
import MediaMessage from '../../../components/Chat/MediaMessage';

//images
import { useSelector } from 'react-redux';
import BasicModal from '../../../components/Dashboard/BasicModal';
import { SVGICON } from '../../../components/Dashboard/Content';
import course1 from './../../../../images/coures/1.jpg';
import course5 from './../../../../images/coures/5.jpg';
import {
  formatMessageDate,
  formatMessageTime,
  groupMessagesByDate,
  shouldGroupMessages
} from '../../../../utils/dateUtils.js'; // Import the new utilities

export const StudentChat = () => {
  const childRef = useRef();
  const chatContainerRef = useRef(null);

  // Get current user from Redux store
  const currentUser = useSelector(state => state.auth.currentUser);
  const studentId = useSelector(state => state.auth.currentUser?.id);

  const [chatBoxes, setChatBoxes] = useState([]);
  const [availableTutors, setAvailableTutors] = useState([]);
  const [selectedChatBox, setSelectedChatBox] = useState(null);
  const [messages, setMessages] = useState([]);
  const [groupedMessages, setGroupedMessages] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [tutorsLoading, setTutorsLoading] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [recordingAudio, setRecordingAudio] = useState(false);

  // Listen to user's chat boxes with real tutor data
  useEffect(() => {
    if (studentId) {
      const unsubscribe = listenToUserChatBoxes(studentId, 'student', (userChatBoxes) => {
        setChatBoxes(userChatBoxes);
      });

      return () => unsubscribe();
    }
  }, [studentId]);

  // Group messages by date when messages change
  useEffect(() => {
    if (messages.length > 0) {
      const grouped = groupMessagesByDate(messages);
      setGroupedMessages(grouped);
    } else {
      setGroupedMessages({});
    }
  }, [messages]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      const container = chatContainerRef.current;
      setTimeout(() => {
        container.scrollTop = container.scrollHeight;
      }, 100);
    }
  }, [messages, selectedChatBox, groupedMessages]);

  // Load available tutors for new chats
  useEffect(() => {
    const loadAvailableTutors = async () => {
      if (!studentId) return;

      setTutorsLoading(true);
      try {
        const tutors = await getAvailableTutorsForStudent(studentId);
        setAvailableTutors(tutors);
      } catch (error) {
        console.error('Error loading available tutors:', error);
        setAvailableTutors([]);
      } finally {
        setTutorsLoading(false);
      }
    };

    loadAvailableTutors();
  }, [studentId]);

  // Listen to unread message counts
  useEffect(() => {
    if (studentId) {
      const unsubscribe = getUnreadMessageCount(studentId, (count) => {
        console.log('Unread messages count:', count);
        setUnreadCounts(prev => ({ ...prev, total: count }));
      });

      // Cleanup function
      return () => {
        if (unsubscribe && typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    }
  }, [studentId]);

  // Listen to messages when a chat box is selected
  useEffect(() => {
    if (selectedChatBox?.id && studentId) {
      const unsubscribe = listenToMessages(selectedChatBox.id, (chatMessages) => {
        setMessages(chatMessages);
        markMessagesAsRead(selectedChatBox.id, studentId);

        setUnreadCounts(prev => ({
          ...prev,
          [selectedChatBox.id]: 0
        }));
      });

      return () => unsubscribe();
    }
  }, [selectedChatBox, studentId]);

  // Start a new chat
  const startNewChat = async (tutorId, tutorName) => {
    if (!studentId || !tutorId) return;

    try {
      setLoading(true);
      const chatBox = await getOrCreateChatBox(
        studentId,
        tutorId,
        'student',
        'tutor'
      );

      const enrichedChatBox = {
        ...chatBox,
        tutorId: tutorId,
        otherParticipantName: tutorName,
        otherParticipantType: 'tutor',
        otherParticipantImage: null
      };

      setSelectedChatBox(enrichedChatBox);
      childRef.current?.closeModal();
    } catch (error) {
      console.error('Error starting new chat:', error);
    } finally {
      setLoading(false);
    }
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedChatBox || !studentId) return;

    try {
      setLoading(true);

      const receiverId = selectedChatBox.ids.find(id => id !== parseInt(studentId));

      await sendMessage(
        selectedChatBox.id,
        studentId,
        receiverId,
        newMessage,
        'student',
        'NONE'
      );

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add this function to handle audio recording completion
  const handleAudioRecordingComplete = async (audioFile) => {
    if (!audioFile || !selectedChatBox || !currentUser?.id) return;

    try {
      setRecordingAudio(true);

      // Upload audio file
      const uploadResult = await uploadMedia(
        audioFile,
        selectedChatBox.id,
        currentUser.id
      );

      // Get receiver ID
      const receiverId = selectedChatBox.ids.find(id => id !== parseInt(currentUser.id));

      // Send audio message
      await sendMessage(
        selectedChatBox.id,
        currentUser.id,
        receiverId,
        newMessage, // You can keep any text message along with audio
        'student',
        MEDIA_TYPES.AUDIO,
        uploadResult.url,
        uploadResult.duration
      );

      // Clear text message if any
      setNewMessage('');

    } catch (error) {
      console.error('Error sending audio message:', error);
      alert('Failed to send audio message. Please try again.');
    } finally {
      setRecordingAudio(false);
    }
  };

  // Handle enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Get message status text
  const getMessageStatus = (status) => {
    switch (status) {
      case 0: return 'Sent';
      case 1: return 'Delivered';
      case 2: return 'Read';
      default: return 'Sent';
    }
  };

  // In StudentChat.jsx or TutorChat.jsx
  const handleMediaSelect = async (files, mediaTypes) => {
    if (!files || files.length === 0) return;

    try {
      setUploadingMedia(true);

      // Handle multiple files if needed
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const mediaType = mediaTypes[i];

        if (mediaType === MEDIA_TYPES.IMAGE ||
          mediaType === MEDIA_TYPES.VIDEO ||
          mediaType === MEDIA_TYPES.AUDIO) {

          // For file uploads
          const uploadResult = await uploadMedia(file, selectedChatBox.id, currentUser.id);

          // Send message with media
          await sendMessage(
            selectedChatBox.id,
            currentUser.id,
            selectedChatBox.ids.find(id => id !== parseInt(currentUser.id)),
            newMessage,
            'student',
            uploadResult.type,
            uploadResult.url,
            uploadResult.duration
          );
        } else {
          // For non-file media types
          await sendMessage(
            selectedChatBox.id,
            currentUser.id,
            selectedChatBox.ids.find(id => id !== parseInt(currentUser.id)),
            newMessage,
            currentUser.type,
            mediaType,
            null, // No media_path
            null, // No duration
            file   // Pass data as media_data
          );
        }
      }

      setNewMessage('');
    } catch (error) {
      console.error('Error sending media:', error);
      alert('Failed to send media. Please try again.');
    } finally {
      setUploadingMedia(false);
    }
  };

  // Filter chat boxes based on search
  const filteredChatBoxes = chatBoxes.filter(chatBox =>
    chatBox.otherParticipantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    chatBox.last_message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get unread count for a specific chat box
  const getUnreadCountForChat = (chatBoxId) => {
    return unreadCounts.total[chatBoxId] || 0;
  };

  return (
    <>
      <div className="row gx-0 bg-white">
        {/* Left sidebar remains the same */}
        <div className="col-xl-3 col-xxl-4 col-md-5 chat-left-area">
          <div className="card dlab-scroll chat-sidebar rounded-0 mb-0">
            <div className="card-body">
              <div className="message-box d-flex align-items-center justify-content-between border-0">
                <div className="input-group search-area">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search chats..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <span className="input-group-text">
                    <Link to={"#"}>
                      {SVGICON.search}
                    </Link>
                  </span>
                </div>
              </div>

              {/* Chat Boxes List */}
              <div className="chat-tabs">
                <h4>Your Chats</h4>
                <div className="course-details-tab style-2">
                  <ul>
                    {filteredChatBoxes.map((chatBox) => {
                      const unreadCount = getUnreadCountForChat(chatBox.id);
                      return (
                        <li
                          key={chatBox.id}
                          className={`chat-bx chats ${selectedChatBox?.id === chatBox.id ? 'active' : ''}`}
                          onClick={() => setSelectedChatBox(chatBox)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="chat-img">
                            <img
                              src={chatBox.otherParticipantImage || course5}
                              alt={chatBox.otherParticipantName}
                              onError={(e) => { e.target.src = course5; }}
                            />
                          </div>
                          <div className="mid-info">
                            <h4 className="name d-flex align-items-center">
                              {chatBox.otherParticipantName}
                            </h4>
                            <span className="text-muted text-truncate d-block">
                              {chatBox.last_message || 'No messages yet'}
                            </span>
                          </div>
                          <div className="right-info text-end">
                            <p className="mb-1">{formatMessageTime(chatBox.lastMessageTimeFormatted)}</p>
                            {unreadCount > 0 && (
                              <span className="badge bg-primary rounded-pill">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                          </div>
                        </li>
                      );
                    })}

                    {filteredChatBoxes.length === 0 && !loading && (
                      <li className="text-center text-muted py-3">
                        {studentId ? 'No chats yet' : 'Please login to view chats'}
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              {!studentId && (
                <div className="alert alert-warning mt-3">
                  <small>Please login to start chatting with tutors</small>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="col-xl-9 col-xxl-8 col-md-7 chat-mid-area bg-white">
          {selectedChatBox ? (
            <>
              <div className="message-box style-1 d-flex align-items-center">
                <img src={selectedChatBox.otherParticipantImage || course1} alt={selectedChatBox.otherParticipantName} />
                <div className="ms-2 ms-sm-3 flex-1">
                  <h4>{selectedChatBox.otherParticipantName}</h4>
                  <span>
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="8" cy="8" r="8" fill="#4CBC9A"></circle>
                    </svg>
                    {" "}Online
                  </span>
                </div>
                <div className="chat-hamburger ms-auto">
                  <div className="chat-toggle">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 24 24" version="1.1" className="svg-main-icon">
                      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <rect x="0" y="0" width="24" height="24"></rect>
                        <rect fill="#008ddd" x="4" y="4" width="7" height="7" rx="1.5"></rect>
                        <path d="M5.5,13 L9.5,13 C10.3284271,13 11,13.6715729 11,14.5 L11,18.5 C11,19.3284271 10.3284271,20 9.5,20 L5.5,20 C4.67157288,20 4,19.3284271 4,18.5 L4,14.5 C4,13.6715729 4.67157288,13 5.5,13 Z M14.5,4 L18.5,4 C19.3284271,4 20,4.67157288 20,5.5 L20,9.5 C20,10.3284271 19.3284271,11 18.5,11 L14.5,11 C13.6715729,11 13,10.3284271 13,9.5 L13,5.5 C13,4.67157288 13.6715729,4 14.5,4 Z M14.5,13 L18.5,13 C19.3284271,13 20,13.6715729 20,14.5 L20,18.5 C20,19.3284271 19.3284271,20 18.5,20 L14.5,20 C13.6715729,20 13,19.3284271 13,18.5 L13,14.5 C13,13.6715729 13.6715729,13 14.5,13 Z" fill="#666666" opacity="0.8"></path>
                      </g>
                    </svg>
                  </div>
                  {/* <div className="videos-player">
                    <Link to={"#"} className="videos-btn">
                      <svg width="25" height="25" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.9997 8H3.99967C2.52691 8 1.33301 9.19391 1.33301 10.6667V21.3333C1.33301 22.8061 2.52691 24 3.99967 24H19.9997C21.4724 24 22.6663 22.8061 22.6663 21.3333V10.6667C22.6663 9.19391 21.4724 8 19.9997 8Z" stroke="#A098AE" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                        <path d="M30.666 9.33325V22.6666L22.666 15.9999L30.666 9.33325Z" stroke="#A098AE" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                      </svg>
                    </Link>
                  </div> */}
                  {/* <Dropdown className="custom-dropdown">
                    <Dropdown.Toggle as="div" className="i-false btn sharp tp-btn ">
                      {SVGICON.dots}
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="dropdown-menu-end" align="end">
                      <Dropdown.Item>Option 1</Dropdown.Item>
                      <Dropdown.Item>Option 2</Dropdown.Item>
                      <Dropdown.Item>Option 3</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown> */}
                </div>
              </div>

              <div className="chart-content">
                <div
                  className="chat-box-area dlab-scroll"
                  id="chartBox"
                  ref={chatContainerRef}
                  style={{ maxHeight: '500px', overflowY: 'auto' }}
                >
                  {Object.keys(groupedMessages).length > 0 ? (
                    Object.entries(groupedMessages).map(([dateKey, group]) => (
                      <div key={dateKey}>
                        {/* Date Separator */}
                        <div className="date-separator text-center my-3">
                          <span className="badge bg-light text-dark px-3 py-2 rounded-pill">
                            <i className="fas fa-calendar-alt me-2"></i>
                            {group.displayDate}
                          </span>
                        </div>

                        {/* Messages for this date */}
                        {group.messages.map((message, index) => {
                          const isCurrentUser = message.sender_id == parseInt(currentUser?.id);
                          const prevMessage = index > 0 ? group.messages[index - 1] : null;
                          const showAvatar = !shouldGroupMessages(prevMessage, message);
                          const showTime = !shouldGroupMessages(message, group.messages[index + 1]) ||
                            index === group.messages.length - 1;
                          console.log("sender and current user =", message.sender_id, currentUser?.id)

                          return (
                            <div
                              key={message.id}
                              className={`media ${!showAvatar ? 'mt-1' : 'mt-3'} justify-content-${isCurrentUser ? 'end' : 'start'} align-items-start`}
                            >
                              {/* Avatar (only shown when not grouped) */}
                              {/* {!isCurrentUser && showAvatar && (
                                <div className="me-2 flex-shrink-0">
                                  <img
                                    src={selectedChatBox.otherParticipantImage || course5}
                                    alt={selectedChatBox.otherParticipantName}
                                    className="rounded-circle"
                                    style={{ width: '32px', height: '32px', objectFit: 'cover', marginRight: '10px' }}
                                  />
                                </div>
                              )} */}

                              {/* Message Bubble */}
                              <div
                                className={`message-${isCurrentUser ? 'sent' : 'received'} w-auto ${isCurrentUser ? 'me-2' : ''} ${!showAvatar ? 'no-avatar' : ''}`}
                                style={{
                                  // marginLeft: !isCurrentUser && !showAvatar ? '44px' : '0',
                                  // backgroundColor: isCurrentUser ? '#0084ff' : '#f0f0f0',
                                  // color: isCurrentUser ? 'white' : 'black',
                                  // padding: '8px 12px',
                                  // borderRadius: '18px',
                                  // maxWidth: '70%',
                                  // position: 'relative'
                                }}
                              >

                                {message.media_type && message.media_type !== MEDIA_TYPES.NONE ? (
                                  <MediaMessage
                                    message={message}
                                    isCurrentUser={message.sender_id == parseInt(currentUser?.id)}
                                  />
                                ) : (
                                  <p className="mb-1" style={{ wordBreak: 'break-word' }}>
                                    {message.message}
                                  </p>
                                )}
                                {/* Message time and status */}
                                {showTime && (
                                  <div className="d-flex align-items-center justify-content-end mt-1">
                                    <span className="fs-12" style={{
                                      //color: isCurrentUser ? 'rgba(255,255,255,0.7)' : '#999',
                                      fontSize: '11px'
                                    }}>
                                      {formatMessageTime(message.created_at)}
                                    </span>
                                    {isCurrentUser && (
                                      <span className="fs-10 ms-2" style={{
                                        //color: 'rgba(255,255,255,0.7)',
                                        fontSize: '11px'
                                      }}>
                                        {getMessageStatus(message.message_status)}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Current user avatar (right side) */}
                              {/* {isCurrentUser && showAvatar && (
                                <div className="ms-2 flex-shrink-0">
                                  <img
                                    src={currentUser?.imageUrl ? `${process.env.REACT_APP_IMAGE_PATH}${currentUser.imageUrl}` : course5}
                                    alt="You"
                                    className="rounded-circle"
                                    style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                  />
                                </div>
                              )} */}
                            </div>
                          );
                        })}
                      </div>
                    ))
                  ) : messages.length === 0 ? (
                    <div className="text-center text-muted py-5">
                      <i className="fas fa-comments fa-3x mb-3"></i>
                      <h5>No messages yet</h5>
                      <p>Start the conversation!</p>
                    </div>
                  ) : null}
                </div>

                {/* Message Input */}
                {/* Message Input with Attachment Button */}
                <div className="type-massage mt-3">
                  <div className="input-group">
                    <div className="input-group-prepend d-flex align-items-center">
                      <button
                        type="button"
                        className="btn btn-light rounded-circle mr-2"
                        onClick={() => setShowMediaModal(true)}
                        disabled={uploadingMedia || recordingAudio}
                        style={{
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {uploadingMedia ? (
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Uploading...</span>
                          </div>
                        ) : (
                          <i className="bi bi-paperclip" style={{ fontSize: '1.25rem' }}></i>
                        )}
                      </button>

                      <button
                        type="button"
                        className="btn btn-light rounded-circle mr-2"
                        onClick={() => setShowAudioRecorder(true)}
                        disabled={uploadingMedia || recordingAudio}
                        style={{
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <i className="bi bi-mic-fill" style={{ fontSize: '1.25rem' }}></i>
                      </button>
                    </div>

                    <textarea
                      className="form-control"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={loading || uploadingMedia || recordingAudio}
                      rows="1"
                      style={{ borderRadius: '20px', padding: '10px 15px' }}
                    />

                    <div className="input-group-append">
                      <button
                        type="button"
                        className="btn btn-primary rounded-circle ml-2"
                        onClick={handleSendMessage}
                        disabled={(!newMessage.trim() && !recordingAudio) || loading || uploadingMedia}
                        style={{
                          width: '40px',
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {loading ? (
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Sending...</span>
                          </div>
                        ) : (
                          <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.466 0.0554752L0.96597 6.80548C0.816794 6.8708 0.692728 6.98259 0.612274 7.12419C0.53182 7.26578 0.499288 7.42958 0.519529 7.59117C0.53977 7.75276 0.6117 7.90348 0.724592 8.02085C0.837484 8.13822 0.98529 8.21596 1.14597 8.24248L9.14097 9.35923L10.2577 17.3542C10.2807 17.5174 10.3567 17.6685 10.4741 17.7841C10.5915 17.8997 10.7437 17.9735 10.9072 17.994C10.9381 17.9978 10.9691 17.9998 11.0002 18C11.149 17.9999 11.2944 17.9556 11.418 17.8727C11.5415 17.7897 11.6376 17.6719 11.694 17.5342L18.444 1.03423C18.4967 0.897557 18.5087 0.748508 18.4784 0.60517C18.4481 0.461832 18.3769 0.330368 18.2733 0.226735C18.1698 0.123102 18.0384 0.0517551 17.8951 0.0213547C17.7517 -0.0090457 17.6027 0.00280736 17.466 0.0554752ZM11.3587 14.3925L10.5502 8.59048C10.5275 8.42922 10.453 8.27973 10.3379 8.16459C10.2227 8.04944 10.0732 7.9749 9.91197 7.95223L4.10772 7.14148L16.3792 2.12098L11.3587 14.3925Z" fill="#FCFCFC" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Media Attachment Modal */}
                <MediaAttachmentModal
                  show={showMediaModal}
                  onClose={() => setShowMediaModal(false)}
                  onSelectMedia={handleMediaSelect}
                  chatBoxId={selectedChatBox?.id}
                />

                {/* Audio Recorder Modal (overlay) */}
                {showAudioRecorder && (
                  <div className="audio-recorder-overlay" style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050
                  }}>
                    <AudioRecorder
                      onRecordingComplete={handleAudioRecordingComplete}
                      onClose={() => setShowAudioRecorder(false)}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="text-center">
                <i className="fas fa-comments fa-4x text-muted mb-4"></i>
                <h4>Select a chat to start messaging</h4>
                <p className="text-muted">Choose a tutor from the list to begin your conversation</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for starting new chat */}
      <BasicModal
        ref={childRef}
        title="Start New Chat with Tutor"
        content={
          <div>
            <h5>Select a tutor to start chatting:</h5>
            {tutorsLoading ? (
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading tutors...</span>
                </div>
              </div>
            ) : availableTutors.length > 0 ? (
              <div className="mt-3">
                {availableTutors.map(tutor => (
                  <div
                    key={tutor.tutorId}
                    className="d-flex align-items-center p-2 border rounded mb-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => startNewChat(tutor.tutorId, tutor.name)}
                  >
                    <img
                      src={tutor.imagePath ? `${process.env.REACT_APP_IMAGE_PATH}${tutor.imagePath}` : course1}
                      alt={tutor.name}
                      className="rounded-circle me-3"
                      width="40"
                      height="40"
                      onError={(e) => { e.target.src = course1; }}
                    />
                    <div>
                      <div className="fw-bold">{tutor.name}</div>
                      <small className="text-muted">Tutor</small>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-3">
                <p>No available tutors found.</p>
                <small>All tutors you can message are already in your chat list.</small>
              </div>
            )}
          </div>
        }
      />
    </>
  );
};